import { OwnerId } from 'prove-common'

export interface DelegationNode {
  plannedQuantity: number
  samplesTaken?: number
  /** Planned responsibility retained here instead of delegated to children. */
  retainedQuantity?: number
  ownerId: OwnerId
  children: DelegationNode[]
}

const isSameOwner = (a: OwnerId, b: OwnerId): boolean =>
  a.type === b.type && a.owner === b.owner

export const sumPlannedQuantity = (nodes: DelegationNode[]): number =>
  nodes.reduce((sum, node) => sum + node.plannedQuantity, 0)

const withUpdatedChildren = (
  node: DelegationNode,
  children: DelegationNode[]
): DelegationNode => {
  const delegated = sumPlannedQuantity(children)
  const retained = node.retainedQuantity ?? 0
  return {
    ...node,
    children,
    plannedQuantity: retained + delegated,
    retainedQuantity: retained
  }
}

/**
 * Finner fordelingen som ligger på en gitt kjede av ownerId-er.
 */
export const findByOwnerPath = (
  roots: DelegationNode[],
  path: OwnerId[]
): DelegationNode | undefined => {
  let nodes = roots
  let found: DelegationNode | undefined
  for (const ownerId of path) {
    found = nodes.find((node) => isSameOwner(node.ownerId, ownerId))
    if (found == null) return undefined
    nodes = found.children
  }
  return found
}

/**
 * Returns true when a proper ancestor is a persisted terminal delegation.
 * Current organization descendants below that point must remain read-only.
 */
export const hasTerminalDelegationAncestor = (
  roots: DelegationNode[],
  path: OwnerId[]
): boolean => delegationTargetPath(roots, path).length < path.length

/** Stops an organization path at the first persisted terminal delegation. */
export const delegationTargetPath = (
  roots: DelegationNode[],
  path: OwnerId[]
): OwnerId[] => {
  let nodes = roots
  for (let index = 0; index < path.length; index += 1) {
    const ownerId = path[index]
    const node = nodes.find((candidate) =>
      isSameOwner(candidate.ownerId, ownerId)
    )
    if (node == null) return path
    if (node.children.length === 0 && index < path.length - 1)
      return path.slice(0, index + 1)
    nodes = node.children
  }
  return path
}

/**
 * Fordelingene som ligger rett under en gitt sti. Tom sti gir rotnivået.
 */
export const getChildrenAtPath = (
  roots: DelegationNode[],
  path: OwnerId[]
): DelegationNode[] =>
  path.length === 0 ? roots : (findByOwnerPath(roots, path)?.children ?? [])

/** Includes responsibility retained at the scoped root, not just its children. */
export const getDelegationTotals = (
  roots: DelegationNode[],
  path: OwnerId[],
  plannedQuantity: number
) => {
  const root = findByOwnerPath(roots, path)
  const allocated =
    root?.plannedQuantity ?? sumPlannedQuantity(getChildrenAtPath(roots, path))
  return {
    root,
    allocated,
    remainder: Math.max(plannedQuantity - allocated, 0)
  }
}

/**
 * Antall prøver som er tatt i hele undertreet. Vi summerer bladnodene fremfor å
 * lese `samplesTaken` på foreldrene, slik at tallet alltid stemmer med treet vi viser.
 */
export const sumSamplesTaken = (node: DelegationNode): number =>
  (node.samplesTaken ?? 0) +
  node.children.reduce((sum, child) => sum + sumSamplesTaken(child), 0)

const createDelegationChain = (
  path: OwnerId[],
  depth: number,
  quantity: number
): DelegationNode => {
  const ownerId = path[depth]
  const node: DelegationNode = {
    plannedQuantity: quantity,
    ownerId,
    children: []
  }
  if (depth < path.length - 1) {
    node.children = [createDelegationChain(path, depth + 1, quantity)]
  }
  return node
}

const setQuantity = (
  nodes: DelegationNode[],
  path: OwnerId[],
  depth: number,
  quantity: number
): DelegationNode[] => {
  const ownerId = path[depth]
  const existing = nodes.find((node) => isSameOwner(node.ownerId, ownerId))
  if (existing == null) {
    if (quantity <= 0) return nodes
    return [...nodes, createDelegationChain(path, depth, quantity)]
  }
  if (depth === path.length - 1) {
    if (quantity <= 0 && (existing.samplesTaken ?? 0) === 0)
      return nodes.filter((node) => node !== existing)
    return nodes.map((node) =>
      node === existing
        ? {
            ...node,
            plannedQuantity: quantity,
            retainedQuantity:
              node.children.length === 0 && node.retainedQuantity != null
                ? quantity
                : node.retainedQuantity
          }
        : node
    )
  }
  const children = setQuantity(existing.children, path, depth + 1, quantity)
  if (children.length === 0) {
    if ((existing.samplesTaken ?? 0) > 0) {
      return nodes.map((node) =>
        node === existing
          ? { ...node, children: [], retainedQuantity: node.plannedQuantity }
          : node
      )
    }
    return nodes.filter((node) => node !== existing)
  }
  return nodes.map((node) =>
    node === existing ? withUpdatedChildren(node, children) : node
  )
}

/**
 * Setter antall på enheten stien peker på, og oppretter manglende mellomnivåer
 * underveis. Antall 0 fjerner fordelingen, og foreldre som står igjen uten barn
 * fjernes også. Foreldrenes antall settes alltid til summen av barna.
 */
export const setQuantityAtPath = (
  roots: DelegationNode[],
  path: OwnerId[],
  quantity: number
): DelegationNode[] => {
  if (path.length === 0) return roots
  return setQuantity(roots, path, 0, quantity)
}

/**
 * Fordeler et antall så likt som mulig. Resten gis til de første i rekken.
 */
export const distributeEvenly = (total: number, count: number): number[] => {
  if (count <= 0) return []
  const safeTotal = Math.max(0, Math.floor(total))
  const base = Math.floor(safeTotal / count)
  const remainder = safeTotal % count
  return Array.from(
    { length: count },
    (_, index) => base + (index < remainder ? 1 : 0)
  )
}

export const hasMissingOwner = (delegations: DelegationNode[]): boolean => {
  return delegations.some((d) => {
    const missingHere = !d.ownerId.owner || d.ownerId.owner.trim() === ''
    return missingHere || hasMissingOwner(d.children)
  })
}

export const validateDelegations = (
  delegations: DelegationNode[]
): {
  isValid: boolean
  error: string | undefined
} => {
  const missingOwner = hasMissingOwner(delegations)
  if (missingOwner) {
    return {
      isValid: false,
      error: 'Alle fordelinger må ha valgt enhet før lagring.'
    }
  }
  const invalidQuantity = flattenAllDelegations(delegations).some(
    ({ plannedQuantity }) =>
      !Number.isFinite(plannedQuantity) ||
      !Number.isInteger(plannedQuantity) ||
      plannedQuantity < 0
  )
  if (invalidQuantity) {
    return {
      isValid: false,
      error: 'Antall må være et heltall på 0 eller mer for alle fordelinger.'
    }
  }
  return { isValid: true, error: undefined }
}

const flattenAllDelegations = (
  delegations: DelegationNode[]
): DelegationNode[] =>
  delegations.flatMap((delegation) => [
    delegation,
    ...flattenAllDelegations(delegation.children)
  ])
